import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function GuideDownloadSection() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [errors, setErrors] = useState({ name: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = { name: '', email: '' };
    let isValid = true;

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
      isValid = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit form data
      const formElement = e.target as HTMLFormElement;
      const formDataToSubmit = new FormData(formElement);
      
      await fetch('https://readdy.ai/api/form/d6h7dl4thj2bbt0jp7pg', {
        method: 'POST',
        body: new URLSearchParams(formDataToSubmit as any),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      // Show success state
      setIsSuccess(true);

      // Trigger PDF download
      setTimeout(() => {
        triggerPDFDownload();
      }, 500);

      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({ name: '', email: '' });
        setIsSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const triggerPDFDownload = () => {
    // Create a simple PDF content placeholder
    const pdfContent = `
Tokyo Minpaku Startup Guide (2026 Edition)

Thank you for downloading our comprehensive guide!

This guide covers:
• Difference between Minpaku and Ryokan license
• Basic licensing steps
• Estimated timeline
• Common mistakes
• Revenue estimation overview
• When to choose management service

For the complete interactive guide, please visit our website.

Contact us for personalized consultation:
Email: info@tokyominpaku.com
Phone: +81-3-XXXX-XXXX

© 2026 Tokyo Minpaku Support
    `;

    const blob = new Blob([pdfContent], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Tokyo-Minpaku-Startup-Guide-2026.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          {/* Decorative Border Top */}
          <div className="h-1 w-32 bg-gradient-to-r from-amber-600 to-orange-600 mx-auto mb-8"></div>

          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl mb-6 shadow-lg">
              <i className="ri-file-download-line text-4xl text-white"></i>
            </div>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-slate-900 mb-6 leading-tight">
              Download the Free Guide Before You Start Your Airbnb Business in Tokyo
            </h2>
            <p className="text-xl text-slate-700 leading-relaxed max-w-3xl mx-auto">
              Get instant access to our comprehensive 2026 edition guide covering everything from licensing differences to revenue estimation. Written by active Tokyo hosts with real operational experience.
            </p>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-3xl shadow-2xl border-2 border-amber-200 overflow-hidden">
            <div className="p-8 md:p-12">
              {/* Guide Contents Preview */}
              <div className="mb-10">
                <h3 className="text-2xl font-serif font-bold text-slate-900 mb-6 text-center">
                  What's Inside the Guide:
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    { icon: 'ri-file-list-3-line', text: 'Minpaku vs Ryokan License Explained' },
                    { icon: 'ri-roadmap-line', text: 'Step-by-Step Licensing Process' },
                    { icon: 'ri-time-line', text: 'Realistic Timeline & Milestones' },
                    { icon: 'ri-error-warning-line', text: 'Common Mistakes to Avoid' },
                    { icon: 'ri-money-dollar-circle-line', text: 'Revenue Estimation Framework' },
                    { icon: 'ri-team-line', text: 'When to Use Management Services' },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 hover:shadow-md transition-all duration-300"
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex-shrink-0">
                        <i className={`${item.icon} text-xl text-white`}></i>
                      </div>
                      <p className="text-slate-800 font-medium pt-2">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-amber-300 to-transparent mb-10"></div>

              {/* Form Section */}
              {!isSuccess ? (
                <div>
                  <h3 className="text-2xl font-serif font-bold text-slate-900 mb-6 text-center">
                    Enter Your Details to Download
                  </h3>
                  <form
                    id="guide-download-form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="space-y-6"
                  >
                    {/* Name Field */}
                    <div>
                      <label htmlFor="guide-name" className="block text-sm font-semibold text-slate-700 mb-2">
                        Your Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="guide-name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full px-5 py-4 text-lg border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all ${
                          errors.name ? 'border-red-500' : 'border-slate-300'
                        }`}
                        placeholder="Taro Yamada"
                      />
                      {errors.name && (
                        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                          <i className="ri-error-warning-line"></i>
                          {errors.name}
                        </p>
                      )}
                    </div>

                    {/* Email Field */}
                    <div>
                      <label htmlFor="guide-email" className="block text-sm font-semibold text-slate-700 mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="guide-email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-5 py-4 text-lg border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all ${
                          errors.email ? 'border-red-500' : 'border-slate-300'
                        }`}
                        placeholder="your.email@example.com"
                      />
                      {errors.email && (
                        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                          <i className="ri-error-warning-line"></i>
                          {errors.email}
                        </p>
                      )}
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-amber-600 to-orange-600 text-white text-lg font-bold py-5 px-8 rounded-xl hover:from-amber-700 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>
                          Processing...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-download-cloud-line text-2xl"></i>
                          Download Free Guide Now
                        </span>
                      )}
                    </button>
                  </form>
                </div>
              ) : (
                <div className="text-center py-12 animate-fade-in">
                  <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full mb-6 animate-bounce-once">
                    <i className="ri-check-line text-5xl text-white"></i>
                  </div>
                  <h3 className="text-3xl font-serif font-bold text-slate-900 mb-4">
                    Success! Your Download Has Started
                  </h3>
                  <p className="text-lg text-slate-700 mb-6">
                    Check your downloads folder for the Tokyo Minpaku Startup Guide.
                  </p>
                  <p className="text-slate-600">
                    We've also sent a copy to your email with additional resources.
                  </p>
                </div>
              )}

              {/* Trust Indicators */}
              {!isSuccess && (
                <div className="mt-8 pt-8 border-t border-slate-200">
                  <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-full">
                        <i className="ri-check-line text-green-600"></i>
                      </div>
                      <span className="font-medium">100% Free Download</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded-full">
                        <i className="ri-shield-check-line text-blue-600"></i>
                      </div>
                      <span className="font-medium">No Spam, Ever</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center bg-purple-100 rounded-full">
                        <i className="ri-flashlight-line text-purple-600"></i>
                      </div>
                      <span className="font-medium">Instant Access</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-full">
                        <i className="ri-star-line text-orange-600"></i>
                      </div>
                      <span className="font-medium">2026 Updated Edition</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Bottom Note */}
          <div className="text-center mt-8">
            <p className="text-slate-600 text-sm">
              By downloading, you agree to receive occasional updates about Tokyo Minpaku regulations and opportunities. Unsubscribe anytime.
            </p>
          </div>

          {/* Decorative Border Bottom */}
          <div className="h-1 w-32 bg-gradient-to-r from-orange-600 to-amber-600 mx-auto mt-8"></div>
        </div>
      </div>
    </section>
  );
}